/**
 * GRID-OS Digital Friction Check — application
 * ---------------------------------------------------------------------------
 * Depends on: grid-os-assessment-config.js (window.GridOsAssessmentConfig)
 *
 * Everything content-related lives in the config file. This file contains the
 * scoring engine, state handling, rendering, analytics and the HubSpot
 * submission. It exposes exactly one global (window.GridOsAssessment) and
 * registers one custom element (<grid-os-assessment>).
 *
 * Privacy rules enforced here:
 *   - No personal data is ever pushed to window.dataLayer.
 *   - No personal data is ever written to sessionStorage.
 *   - No personal data is ever written to console, including in debug mode.
 *   - GA4 receives the score band, never the exact score.
 *   - All dynamic text is written with textContent, never innerHTML.
 */

window.GridOsAssessment = (function () {
  'use strict';

  var CFG = window.GridOsAssessmentConfig;

  /**
   * Interface string lookup with {placeholder} substitution.
   *
   * Never build a user-facing sentence by concatenation: word order, plural
   * forms and punctuation differ by language. Put the whole sentence in the
   * string table with placeholders and substitute here.
   */
  function T(key, vars) {
    var text = (CFG.ui && CFG.ui[key]) || '';
    if (!vars) return text;
    return text.replace(/\{(\w+)\}/g, function (match, name) {
      return Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : match;
    });
  }

  /* =========================================================================
   * 1. Scoring engine — pure functions, no DOM, no network.
   * ====================================================================== */

  var Scoring = {
    /** Points for a single answer code. Unknown codes score zero. */
    pointsFor: function (code) {
      return Object.prototype.hasOwnProperty.call(CFG.points, code) ? CFG.points[code] : 0;
    },

    /** Band object for a 0–20 area score. */
    bandFor: function (areaScore) {
      for (var i = 0; i < CFG.areaBands.length; i++) {
        if (areaScore >= CFG.areaBands[i].min && areaScore <= CFG.areaBands[i].max) {
          return CFG.areaBands[i];
        }
      }
      return CFG.areaBands[0];
    },

    /** Profile object for a 0–100 total, before the critical-area override. */
    profileFor: function (total) {
      for (var i = 0; i < CFG.profiles.length; i++) {
        if (total >= CFG.profiles[i].min && total <= CFG.profiles[i].max) {
          return CFG.profiles[i];
        }
      }
      return CFG.profiles[0];
    },

    profileById: function (id) {
      for (var i = 0; i < CFG.profiles.length; i++) {
        if (CFG.profiles[i].id === id) return CFG.profiles[i];
      }
      return null;
    },

    /**
     * Compute the full result.
     * @param {Object} answers map of questionId -> answer code ('A'..'E')
     * @returns {Object|null} null if any question is unanswered
     */
    compute: function (answers) {
      var i;

      for (i = 0; i < CFG.questions.length; i++) {
        var qid = CFG.questions[i].id;
        if (!answers || !answers[qid]) return null;
      }

      // Area totals, in canonical order.
      var areaTotals = {};
      for (i = 0; i < CFG.areas.length; i++) areaTotals[CFG.areas[i].id] = 0;

      var total = 0;
      var answerDetail = [];

      for (i = 0; i < CFG.questions.length; i++) {
        var q = CFG.questions[i];
        var code = answers[q.id];
        var pts = Scoring.pointsFor(code);
        areaTotals[q.area] += pts;
        total += pts;
        answerDetail.push({
          questionId: q.id,
          questionNumber: q.number,
          areaId: q.area,
          code: code,
          points: pts,
          hubspotProperty: q.hubspotProperty
        });
      }

      // Build the per-area result list in canonical order.
      var areas = [];
      for (i = 0; i < CFG.areas.length; i++) {
        var area = CFG.areas[i];
        var score = areaTotals[area.id];
        var band = Scoring.bandFor(score);
        areas.push({
          index: i,
          id: area.id,
          number: area.number,
          name: area.name,
          shortName: area.shortName,
          // English originals. Everything leaving the browser uses these, so a
          // German completion and an English one land as the same CRM value.
          wireName: area.wireName,
          wireShortName: area.wireShortName,
          score: score,
          max: 20,
          percent: (score / 20) * 100,
          band: band,
          bandId: band.id,
          bandLabel: band.label,
          wireBandLabel: band.wireLabel,
          result: CFG.areaResults[area.id][band.id],
          recommendation: CFG.areaRecommendations[area.id][band.id],
          wireRecommendation: CFG.wireAreaRecommendations[area.id][band.id],
          hubspotScoreProperty: area.hubspotScoreProperty,
          hubspotStatusProperty: area.hubspotStatusProperty
        });
      }

      var criticalAreas = areas.filter(function (a) { return a.bandId === 'critical'; });

      // Profile, then the spec §3 override.
      var calculatedProfile = Scoring.profileFor(total);
      var finalProfile = calculatedProfile;
      var criticalFriction = false;

      if (
        calculatedProfile.id === CFG.criticalOverride.appliesToProfileId &&
        criticalAreas.length > 0
      ) {
        finalProfile = Scoring.profileById(CFG.criticalOverride.downgradeToProfileId);
        criticalFriction = true;
      }

      var systemicFriction = criticalAreas.length >= CFG.systemicFrictionThreshold;

      // Ranking. Sorts are stable on `index`, so canonical order breaks ties
      // deterministically — the same answers always produce the same result.
      var descending = areas.slice().sort(function (a, b) {
        return b.score - a.score || a.index - b.index;
      });
      var ascending = areas.slice().sort(function (a, b) {
        return a.score - b.score || a.index - b.index;
      });

      // Special case: every area scores identically. Without this, the same
      // area would be reported as both the strongest and the primary friction.
      var allAreasEqual = descending[0].score === ascending[0].score;
      var strongest, primary, secondary;

      if (allAreasEqual) {
        strongest = areas[0];
        primary = areas[areas.length - 1];
        secondary = areas[areas.length - 2];
      } else {
        strongest = descending[0];
        primary = ascending[0];
        secondary = ascending[1];
      }

      return {
        version: CFG.version,
        language: CFG.language,
        completedAt: new Date().toISOString(),

        totalScore: total,
        scoreBand: finalProfile.scoreBand,

        calculatedProfile: calculatedProfile,
        finalProfile: finalProfile,
        criticalFriction: criticalFriction,
        criticalAreas: criticalAreas,
        systemicFriction: systemicFriction,
        allAreasEqual: allAreasEqual,

        areas: areas,
        strongest: strongest,
        primaryFriction: primary,
        secondaryFriction: secondary,

        strongestTied: !allAreasEqual && descending.length > 1 && descending[1].score === descending[0].score,
        primaryTied: !allAreasEqual && ascending.length > 1 && ascending[1].score === ascending[0].score,
        secondaryTied: !allAreasEqual && ascending.length > 2 && ascending[2].score === ascending[1].score,

        // Spec §6: exactly three priorities, in this order.
        recommendations: [
          finalProfile.recommendation,
          primary.recommendation,
          secondary.recommendation
        ],
        // The brief asks for one immediate next action. The primary-friction
        // recommendation is the highest-leverage of the three.
        nextAction: primary.recommendation,

        // The same three, in English, for the CRM summary.
        wireRecommendations: [
          finalProfile.wireRecommendation,
          primary.wireRecommendation,
          secondary.wireRecommendation
        ],
        wireNextAction: primary.wireRecommendation,

        answers: answerDetail
      };
    }
  };

  /* =========================================================================
   * 2. Attribution — read, sanitise, cap. Never trusted, never rendered raw.
   * ====================================================================== */

  var Attribution = {
    sanitise: function (value) {
      if (typeof value !== 'string') return '';
      return value
        .replace(CFG.attributionAllowed, '')
        .slice(0, CFG.attributionMaxLength)
        .trim();
    },

    collect: function () {
      var out = {};
      var params;

      try {
        params = new URLSearchParams(window.location.search);
      } catch (e) {
        return out;
      }

      for (var i = 0; i < CFG.attributionParams.length; i++) {
        var def = CFG.attributionParams[i];
        var raw = params.get(def.param);
        if (raw) {
          var clean = Attribution.sanitise(raw);
          if (clean) out[def.param] = clean;
        }
      }

      // No utm_* on this URL: fall back to the first external campaign source
      // of the session, remembered by snippets/gridos-attribution.liquid (only
      // written with analytics consent, never overwritten). URL values always
      // win; assessment_source / source_page / content_id are not touched here.
      var hasUtm = false;
      for (var u = 0; u < CFG.attributionParams.length; u++) {
        var up = CFG.attributionParams[u].param;
        if (up.indexOf('utm_') === 0 && out[up]) { hasUtm = true; break; }
      }
      if (!hasUtm) {
        var first = null;
        try {
          first = JSON.parse(window.sessionStorage.getItem('gridos_attr_v1') || 'null');
        } catch (e) {
          first = null;
        }
        if (first && typeof first === 'object') {
          for (var f = 0; f < CFG.attributionParams.length; f++) {
            var fp = CFG.attributionParams[f].param;
            if (fp.indexOf('utm_') === 0 && first[fp]) {
              var fv = Attribution.sanitise(String(first[fp]));
              if (fv) out[fp] = fv;
            }
          }
        }
      }

      // No campaign parameters in the URL. The main menu uses proper internal
      // Shopify links so they keep the /de/ locale prefix, and Shopify does not
      // allow a query string on those — so the origin is recovered from the
      // referrer instead. Without this, every visit arriving from our own
      // navigation would be recorded as "direct", which is indistinguishable
      // from someone typing the address by hand.
      if (!out.assessment_source) {
        var ref = null;
        try {
          ref = document.referrer ? new URL(document.referrer) : null;
        } catch (e) {
          ref = null;
        }
        if (ref && ref.origin === window.location.origin) {
          out.assessment_source = 'internal_nav';
          if (!out.source_page) {
            out.source_page = Attribution.sanitise(ref.pathname) || 'internal';
          }
        } else {
          out.assessment_source = ref ? 'referral' : 'direct';
        }
      }

      // Referrer and page URL are captured for HubSpot only, never for GA4.
      out._referrer = Attribution.sanitise(document.referrer || '');
      out._pageUrl = Attribution.sanitise(
        window.location.origin + window.location.pathname
      );
      out._language = CFG.language;

      return out;
    }
  };

  /* =========================================================================
   * 3. Analytics — dataLayer only. Nothing here may carry personal data.
   * ====================================================================== */

  function Analytics(options) {
    this.debug = !!options.debug;
    this.source = options.source || {};
    this.fired = {};
  }

  Analytics.prototype._base = function () {
    return {
      assessment_name: CFG.assessmentName,
      assessment_version: CFG.version,
      language: CFG.language,
      assessment_source: this.source.assessment_source || 'direct',
      source_page: this.source.source_page || ''
    };
  };

  /**
   * @param {string} name event name
   * @param {Object} params non-personal parameters only
   * @param {boolean} once fire at most one time per session
   */
  Analytics.prototype.push = function (name, params, once) {
    if (once) {
      if (this.fired[name]) return;
      this.fired[name] = true;
    }

    var payload = this._base();
    if (params) {
      for (var key in params) {
        if (Object.prototype.hasOwnProperty.call(params, key)) {
          payload[key] = params[key];
        }
      }
    }
    payload.event = name;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);

    if (this.debug) {
      // Safe: this object is assembled from config and scores only.
      window.console.info('[GRID-OS] dataLayer', payload);
    }
  };

  /** utm_* values from the collected attribution, for generate_lead only. */
  Analytics.prototype.utmParams = function (attribution) {
    var out = {};
    var a = attribution || {};
    for (var i = 0; i < CFG.attributionParams.length; i++) {
      var p = CFG.attributionParams[i].param;
      if (p.indexOf('utm_') === 0 && a[p]) out[p] = a[p];
    }
    return out;
  };

  /** Result parameters shared by every post-completion event. */
  Analytics.prototype.resultParams = function (result) {
    return {
      overall_score_band: result.scoreBand,
      profile_id: result.finalProfile.id,
      calculated_profile_id: result.calculatedProfile.id,
      critical_friction: result.criticalFriction ? 'true' : 'false',
      systemic_friction: result.systemicFriction ? 'true' : 'false',
      strongest_area: result.strongest.id,
      primary_friction_area: result.primaryFriction.id,
      secondary_friction_area: result.secondaryFriction.id
    };
  };

  /* =========================================================================
   * 4. Session storage — answers only. Cleared on completion and on restart.
   * ====================================================================== */

  var Storage = {
    available: function () {
      try {
        var k = '__gridos_test__';
        window.sessionStorage.setItem(k, '1');
        window.sessionStorage.removeItem(k);
        return true;
      } catch (e) {
        return false;
      }
    },

    read: function () {
      if (!Storage.available()) return {};
      try {
        var raw = window.sessionStorage.getItem(CFG.storageKey);
        if (!raw) return {};
        var parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return {};

        // Only accept known question ids and valid answer codes.
        var clean = {};
        for (var i = 0; i < CFG.questions.length; i++) {
          var qid = CFG.questions[i].id;
          var code = parsed[qid];
          if (typeof code === 'string' && Object.prototype.hasOwnProperty.call(CFG.points, code)) {
            clean[qid] = code;
          }
        }
        return clean;
      } catch (e) {
        return {};
      }
    },

    write: function (answers) {
      if (!Storage.available()) return;
      try {
        window.sessionStorage.setItem(CFG.storageKey, JSON.stringify(answers));
      } catch (e) { /* quota or private mode — answers simply are not restored */ }
    },

    clear: function () {
      if (!Storage.available()) return;
      try {
        window.sessionStorage.removeItem(CFG.storageKey);
      } catch (e) { /* no-op */ }
    }
  };

  /* =========================================================================
   * 4b. Result summary — one readable block for sales conversations.
   *
   * The other ~38 properties are for filtering, reporting and workflows. This
   * one is for a human reading the contact record before a call. Deliberately
   * plain text with no alignment tricks: HubSpot renders the property in a
   * proportional font, so padded columns would not line up.
   * ====================================================================== */

  var Summary = {
    /**
     * English label for the optional self-declared priority. Resolved from the
     * area list rather than the select option, because option labels are
     * translated and this line is read by a person preparing for a call.
     */
    priorityLabel: function (value) {
      if (!value) return '';
      for (var i = 0; i < CFG.areas.length; i++) {
        if (CFG.areas[i].id === value) return CFG.areas[i].wireName;
      }
      return value;
    },

    /** "16 Aug 2026" from an ISO timestamp, without pulling in a date library. */
    shortDate: function (iso) {
      var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      var d = new Date(iso);
      if (isNaN(d.getTime())) return '';
      return d.getUTCDate() + ' ' + months[d.getUTCMonth()] + ' ' + d.getUTCFullYear();
    },

    /** Full name, for the list of all five. */
    area: function (a) {
      return a.wireName + ': ' + a.score + '/20 — ' + a.wireBandLabel;
    },

    /** Short name in brackets, for lines that already carry a label. */
    areaShort: function (a) {
      return a.wireShortName + ' (' + a.score + '/20, ' + a.wireBandLabel + ')';
    },

    build: function (result, lead, consentGiven) {
      var lines = [];
      var i;

      lines.push('GRID-OS DIGITAL FRICTION CHECK');
      lines.push('Score ' + result.totalScore + '/100 — ' + result.finalProfile.wireLabel);
      lines.push('');

      lines.push('WHERE TO FOCUS');
      if (result.allAreasEqual) {
        lines.push('All five areas score evenly. No single constraint stands out, so');
        lines.push('sequencing matters more than picking one area.');
      } else {
        lines.push('Strongest — ' + Summary.areaShort(result.strongest));

        // Only call something friction if it actually is. "Secondary friction"
        // is defined as the second-lowest area, which on a strong result can
        // be an area scoring 20/20 — printing "Friction 2 — Leadership
        // (20/20, Aligned)" in a sales briefing would be actively misleading.
        var friction = [result.primaryFriction, result.secondaryFriction]
          .filter(function (a) { return a.bandId !== 'aligned'; });

        if (friction.length === 0) {
          lines.push('No area is in friction — all five are aligned.');
        } else {
          for (var f = 0; f < friction.length; f++) {
            lines.push('Friction ' + (f + 1) + ' — ' + Summary.areaShort(friction[f]));
          }
        }
      }
      lines.push('');

      lines.push('ALL FIVE AREAS');
      for (i = 0; i < result.areas.length; i++) {
        lines.push(result.areas[i].number + ' ' + Summary.area(result.areas[i]));
      }
      lines.push('');

      if (result.criticalFriction || result.systemicFriction) {
        lines.push('FLAGS');
        if (result.criticalFriction) {
          lines.push('Critical friction — scored in the top band but at least one area');
          lines.push('is critical, so the profile was downgraded to ' + result.finalProfile.wireLabel + '.');
        }
        if (result.systemicFriction) {
          lines.push('Systemic friction — ' + result.criticalAreas.length + ' of ' +
            result.areas.length + ' areas are critical. Likely a systemic');
          lines.push('constraint rather than a local one.');
        }
        lines.push('');
      }

      lines.push('FIRST THREE PRIORITIES');
      for (i = 0; i < result.wireRecommendations.length; i++) {
        lines.push((i + 1) + '. ' + result.wireRecommendations[i]);
      }
      lines.push('');

      lines.push('START HERE');
      lines.push(result.wireNextAction);
      lines.push('');

      lines.push('CONTEXT');
      var declared = Summary.priorityLabel(lead && lead.primary_priority);
      if (declared) lines.push('They said their priority is: ' + declared);
      lines.push('Marketing consent: ' + (consentGiven ? 'Yes' : 'No'));
      var when = Summary.shortDate(result.completedAt);
      if (when) lines.push('Completed: ' + when);
      lines.push('Version: ' + result.version);
      lines.push('Answered in: ' + result.language);

      return lines.join('\n');
    }
  };

  /* =========================================================================
   * 5. HubSpot submission.
   * Uses the public Forms submission endpoint, which requires no secret. The
   * portal id and form GUID are public identifiers and are safe in theme code.
   * ====================================================================== */

  var HubSpot = {
    endpoint: function (portalId, formGuid) {
      return 'https://api.hsforms.com/submissions/v3/integration/submit/' +
        encodeURIComponent(portalId) + '/' + encodeURIComponent(formGuid);
    },

    /** Reads the HubSpot visitor cookie so the submission joins the contact timeline. */
    hutk: function () {
      var match = document.cookie.match(/(^|;)\s*hubspotutk\s*=\s*([^;]+)/);
      return match ? match[2] : '';
    },

    /** Builds the full field payload from the result, lead data and attribution. */
    buildFields: function (result, lead, attribution, consentGiven) {
      var fields = [];
      var i;

      function add(name, value) {
        if (value === undefined || value === null || value === '') return;
        fields.push({ name: name, value: String(value) });
      }

      // Contact fields.
      for (i = 0; i < CFG.leadFields.length; i++) {
        var def = CFG.leadFields[i];
        add(def.hubspotProperty, lead[def.id]);
      }

      // Core assessment.
      add('grid_os_total_score', result.totalScore);
      add('grid_os_calculated_profile', result.calculatedProfile.wireLabel);
      add('grid_os_final_profile', result.finalProfile.wireLabel);
      add('grid_os_critical_friction', result.criticalFriction ? 'true' : 'false');
      add('grid_os_critical_areas', result.criticalAreas.map(function (a) { return a.wireShortName; }).join('; '));
      add('grid_os_strongest_area', result.strongest.wireShortName);
      add('grid_os_primary_friction_area', result.primaryFriction.wireShortName);
      add('grid_os_secondary_friction_area', result.secondaryFriction.wireShortName);
      add('grid_os_assessment_version', result.version);
      add('grid_os_assessment_status', 'completed');
      add('grid_os_completion_date', result.completedAt);
      add('grid_os_language', result.language);

      // Area scores and statuses.
      for (i = 0; i < result.areas.length; i++) {
        var area = result.areas[i];
        add(area.hubspotScoreProperty, area.score);
        add(area.hubspotStatusProperty, area.wireBandLabel);
      }

      // Raw answers, as stable codes.
      for (i = 0; i < result.answers.length; i++) {
        add(result.answers[i].hubspotProperty, result.answers[i].code);
      }

      // Attribution.
      for (i = 0; i < CFG.attributionParams.length; i++) {
        var ap = CFG.attributionParams[i];
        add(ap.hubspotProperty, attribution[ap.param]);
      }
      add('grid_os_source_url', attribution._pageUrl);

      // Consent.
      add(CFG.consent.marketing.hubspotProperty, consentGiven ? 'true' : 'false');

      // Human-readable briefing. Everything above is machine-readable and
      // spread across ~38 properties, which is unusable two minutes before a
      // call. This single field is what a person actually reads.
      add('grid_os_result_summary', Summary.build(result, lead, consentGiven));

      return fields;
    },

    /**
     * @returns {Promise<{ok: boolean, status: number, testMode: boolean}>}
     */
    submit: function (config, result, lead, attribution, consentGiven) {
      var fields = HubSpot.buildFields(result, lead, attribution, consentGiven);

      var body = {
        fields: fields,
        context: {
          pageUri: attribution._pageUrl,
          pageName: CFG.assessmentName
        }
      };

      var hutk = HubSpot.hutk();
      if (hutk) body.context.hutk = hutk;

      // GDPR. Without this the contact is created but can never be emailed:
      // the portal only sends to contacts that have a recorded legal basis.
      // Each text is the exact sentence the visitor read on this form, in the
      // language they read it in — the record must match what was shown.
      body.legalConsentOptions = {
        consent: {
          consentToProcess: true,
          text: String(T('privacyBody') || '').trim(),
          communications: [
            {
              value: true,
              subscriptionTypeId: CFG.legalConsent.resultsSubscriptionTypeId,
              text: String(T('privacyBody') || '').trim()
            },
            {
              value: !!consentGiven,
              subscriptionTypeId: CFG.legalConsent.marketingSubscriptionTypeId,
              text: CFG.consent.marketing.label
            }
          ]
        }
      };

      if (config.testMode || !config.portalId || !config.formGuid) {
        if (config.debug) {
          window.console.info(
            '[GRID-OS] Test mode — submission NOT sent. Field count: ' + fields.length +
            '. Non-personal field names: ' +
            fields
              .map(function (f) { return f.name; })
              .filter(function (n) { return n.indexOf('grid_os_') === 0; })
              .join(', ')
          );
        }
        return Promise.resolve({ ok: true, status: 0, testMode: true });
      }

      return fetch(HubSpot.endpoint(config.portalId, config.formGuid), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      }).then(function (response) {
        return { ok: response.ok, status: response.status, testMode: false };
      });
    }
  };

  /* =========================================================================
   * 6. Small DOM helpers. All text goes through textContent.
   * ====================================================================== */

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  }

  function clear(node) {
    while (node.firstChild) node.removeChild(node.firstChild);
  }

  function isValidEmail(value) {
    // Deliberately permissive: reject the obviously broken, accept the rest
    // and let HubSpot be the authority.
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);
  }

  function isFreeMailDomain(value) {
    var domain = String(value).split('@')[1] || '';
    return [
      'gmail.com', 'googlemail.com', 'yahoo.com', 'yahoo.co.uk', 'hotmail.com',
      'hotmail.co.uk', 'outlook.com', 'live.com', 'aol.com', 'icloud.com',
      'me.com', 'gmx.de', 'gmx.net', 'web.de', 't-online.de', 'proton.me',
      'protonmail.com', 'mail.com', 'yandex.com'
    ].indexOf(domain.toLowerCase()) !== -1;
  }

  /* =========================================================================
   * 7. The controller.
   * ====================================================================== */

  function Assessment(root) {
    this.root = root;
    this.settings = this.readSettings();
    this.answers = Storage.read();
    this.attribution = Attribution.collect();
    this.analytics = new Analytics({
      debug: this.settings.debug,
      source: this.attribution
    });

    this.currentIndex = 0;
    this.result = null;
    this.submitting = false;
    this.submitted = false;
    this.stage = 'intro';

    this.stageEl = root.querySelector('[data-gridos-stage]');
    this.introEl = root.querySelector('[data-gridos-intro]');
    this.liveEl = root.querySelector('[data-gridos-live]');
    this.startButtons = root.querySelectorAll('[data-gridos-start]');

    this.bind();
    this.analytics.push(CFG.events.view, {}, true);
  }

  Assessment.prototype.readSettings = function () {
    var d = this.root.dataset;
    return {
      portalId: d.portalId || '',
      formGuid: d.formGuid || '',
      testMode: d.testMode === 'true',
      debug: d.debug === 'true',
      ctaLabel: d.ctaLabel || 'Review my score with a senior digital growth partner',
      ctaUrl: d.ctaUrl || '',
      secondaryCtaLabel: d.secondaryCtaLabel || '',
      secondaryCtaUrl: d.secondaryCtaUrl || '',
      privacyUrl: d.privacyUrl || '',
      leadHeading: d.leadHeading || 'Your score is ready.',
      leadBody: d.leadBody || 'Enter your details to receive your full category breakdown and personalised priorities.',
      leadSubmitLabel: d.leadSubmitLabel || 'Show my full result'
    };
  };

  Assessment.prototype.bind = function () {
    var self = this;
    Array.prototype.forEach.call(this.startButtons, function (btn) {
      btn.addEventListener('click', function (event) {
        event.preventDefault();
        self.start();
      });
    });
  };

  Assessment.prototype.announce = function (message) {
    if (!this.liveEl) return;
    this.liveEl.textContent = '';
    var self = this;
    // Re-setting after a tick makes repeat messages announce reliably.
    window.setTimeout(function () { self.liveEl.textContent = message; }, 60);
  };

  Assessment.prototype.answeredCount = function () {
    var n = 0;
    for (var i = 0; i < CFG.questions.length; i++) {
      if (this.answers[CFG.questions[i].id]) n++;
    }
    return n;
  };

  Assessment.prototype.start = function () {
    // Note: friction_check_start deliberately does NOT fire here. The spec
    // defines the start of the assessment as the first answer to Q1, not the
    // click that opens it, so it fires from setAnswer().

    // Resume at the first unanswered question if answers were restored.
    var resumeAt = 0;
    for (var i = 0; i < CFG.questions.length; i++) {
      if (!this.answers[CFG.questions[i].id]) { resumeAt = i; break; }
      resumeAt = i;
    }

    if (this.introEl) this.introEl.hidden = true;
    this.stage = 'questions';
    this.renderQuestion(resumeAt, true);
  };

  /* ---------------------------------------------------------------------- */

  Assessment.prototype.renderQuestion = function (index, focusHeading) {
    var self = this;
    var q = CFG.questions[index];
    this.currentIndex = index;

    clear(this.stageEl);

    var panel = el('div', 'gridos-dfc__panel');
    panel.setAttribute('data-gridos-panel', 'question');

    /* Progress ---------------------------------------------------------- */
    var answered = this.answeredCount();
    var pct = Math.round((index / CFG.questions.length) * 100);

    var progress = el('div', 'gridos-dfc__progress');

    var progressMeta = el('p', 'gridos-dfc__progress-meta');
    progressMeta.textContent = T('questionProgress', { number: q.number, total: CFG.questions.length });
    progress.appendChild(progressMeta);

    var track = el('div', 'gridos-dfc__progress-track');
    track.setAttribute('role', 'progressbar');
    track.setAttribute('aria-valuemin', '0');
    track.setAttribute('aria-valuemax', String(CFG.questions.length));
    track.setAttribute('aria-valuenow', String(index));
    track.setAttribute('aria-valuetext', T('questionProgress', { number: q.number, total: CFG.questions.length }));
    var bar = el('div', 'gridos-dfc__progress-bar');
    bar.style.width = pct + '%';
    track.appendChild(bar);
    progress.appendChild(track);

    panel.appendChild(progress);

    /* Question ---------------------------------------------------------- */
    var area = CFG.areas.filter(function (a) { return a.id === q.area; })[0];

    var fieldset = el('fieldset', 'gridos-dfc__fieldset');
    var legend = el('legend', 'gridos-dfc__legend');

    var eyebrow = el('span', 'gridos-dfc__eyebrow', area.number + ' — ' + area.name);
    var heading = el('span', 'gridos-dfc__question');
    heading.textContent = q.text;
    heading.id = 'gridos-q-' + q.id;
    heading.setAttribute('tabindex', '-1');

    legend.appendChild(eyebrow);
    legend.appendChild(heading);
    fieldset.appendChild(legend);

    var errorId = 'gridos-error-' + q.id;
    var error = el('p', 'gridos-dfc__error');
    error.id = errorId;
    error.hidden = true;

    var list = el('div', 'gridos-dfc__options');

    q.answers.forEach(function (answer) {
      var inputId = 'gridos-' + q.id + '-' + answer.code;

      var wrap = el('div', 'gridos-dfc__option');

      var input = document.createElement('input');
      input.type = 'radio';
      input.name = 'gridos-' + q.id;
      input.id = inputId;
      input.value = answer.code;
      input.className = 'gridos-dfc__radio';
      if (self.answers[q.id] === answer.code) input.checked = true;

      var label = document.createElement('label');
      label.className = 'gridos-dfc__option-label';
      label.setAttribute('for', inputId);

      var marker = el('span', 'gridos-dfc__marker');
      marker.setAttribute('aria-hidden', 'true');
      var text = el('span', 'gridos-dfc__option-text', answer.text);

      label.appendChild(marker);
      label.appendChild(text);

      input.addEventListener('change', function () {
        self.setAnswer(q, answer.code);
        error.hidden = true;
        wrap.parentNode.querySelectorAll('.gridos-dfc__option').forEach(function (o) {
          o.classList.remove('is-selected');
        });
        wrap.classList.add('is-selected');
      });

      if (self.answers[q.id] === answer.code) wrap.classList.add('is-selected');

      wrap.appendChild(input);
      wrap.appendChild(label);
      list.appendChild(wrap);
    });

    fieldset.appendChild(list);
    fieldset.appendChild(error);
    panel.appendChild(fieldset);

    /* Navigation -------------------------------------------------------- */
    var nav = el('div', 'gridos-dfc__nav');

    var back = el('button', 'gridos-dfc__button gridos-dfc__button--ghost', T('back'));
    back.type = 'button';
    if (index === 0) back.disabled = true;
    back.addEventListener('click', function () {
      if (index === 0) return;
      self.analytics.push(CFG.events.stepBack, {
        question_id: q.id,
        question_number: q.number,
        area_id: q.area
      });
      self.renderQuestion(index - 1, true);
    });

    var isLast = index === CFG.questions.length - 1;
    var next = el(
      'button',
      'gridos-dfc__button gridos-dfc__button--primary',
      isLast ? T('seeScore') : T('next')
    );
    next.type = 'button';
    next.addEventListener('click', function () {
      if (!self.answers[q.id]) {
        error.hidden = false;
        error.textContent = T('answerRequired');
        var firstRadio = list.querySelector('input');
        if (firstRadio) firstRadio.focus();
        self.announce(T('answerRequiredAnnounce'));
        return;
      }
      if (isLast) {
        self.complete();
      } else {
        self.renderQuestion(index + 1, true);
      }
    });

    nav.appendChild(back);
    nav.appendChild(next);
    panel.appendChild(nav);

    this.stageEl.appendChild(panel);

    this.analytics.push(CFG.events.questionView, {
      question_id: q.id,
      question_number: q.number,
      area_id: q.area
    });

    if (focusHeading) heading.focus();
    this.announce(T('questionAnnounce', { number: q.number, total: CFG.questions.length, text: q.text }));
  };

  Assessment.prototype.setAnswer = function (q, code) {
    var wasAnswered = !!this.answers[q.id];
    this.answers[q.id] = code;
    Storage.write(this.answers);

    this.analytics.push(CFG.events.start, {}, true);

    if (!wasAnswered) {
      var count = this.answeredCount();
      if (Object.prototype.hasOwnProperty.call(CFG.progressMilestones, count)) {
        this.analytics.push(CFG.events.progress, {
          progress_percent: CFG.progressMilestones[count],
          questions_answered: count
        }, false);
      }
    }
  };

  /* ---------------------------------------------------------------------- */

  Assessment.prototype.complete = function () {
    var result = Scoring.compute(this.answers);
    if (!result) return;

    this.result = result;
    this.stage = 'preview';

    this.analytics.push(CFG.events.complete, this.analytics.resultParams(result), true);
    this.renderPreview();
  };

  Assessment.prototype.renderPreview = function () {
    var self = this;
    var r = this.result;

    clear(this.stageEl);

    var panel = el('div', 'gridos-dfc__panel gridos-dfc__panel--result');

    var heading = el('h2', 'gridos-dfc__result-heading', T('scoreHeading'));
    heading.setAttribute('tabindex', '-1');
    panel.appendChild(heading);

    var scoreWrap = el('div', 'gridos-dfc__score');
    var scoreValue = el('p', 'gridos-dfc__score-value');
    scoreValue.appendChild(el('span', 'gridos-dfc__score-number', String(r.totalScore)));
    scoreValue.appendChild(el('span', 'gridos-dfc__score-max', '/100'));
    scoreWrap.appendChild(scoreValue);
    scoreWrap.appendChild(el('p', 'gridos-dfc__score-profile', r.finalProfile.label));
    panel.appendChild(scoreWrap);

    if (r.criticalFriction) {
      var flag = el('p', 'gridos-dfc__flag', CFG.criticalOverride.flagLabel);
      panel.appendChild(flag);
      panel.appendChild(el('p', 'gridos-dfc__flag-note', CFG.criticalOverride.note));
    }

    panel.appendChild(el('p', 'gridos-dfc__result-headline', r.finalProfile.headline));

    var divider = el('hr', 'gridos-dfc__divider');
    divider.setAttribute('aria-hidden', 'true');
    panel.appendChild(divider);

    panel.appendChild(this.buildLeadForm());

    this.stageEl.appendChild(panel);

    this.analytics.push(CFG.events.resultView, this.analytics.resultParams(r), true);
    this.analytics.push(CFG.events.leadFormView, this.analytics.resultParams(r), true);

    heading.focus();
    this.announce(T('previewAnnounce', {
      score: r.totalScore,
      profile: r.finalProfile.label
    }));
  };

  /* ---------------------------------------------------------------------- */

  Assessment.prototype.buildLeadForm = function () {
    var self = this;

    var wrap = el('div', 'gridos-dfc__lead');
    // Hotjar: suppress everything the visitor types or that echoes it back.
    wrap.setAttribute('data-hj-suppress', '');

    wrap.appendChild(el('h3', 'gridos-dfc__lead-heading', this.settings.leadHeading));
    wrap.appendChild(el('p', 'gridos-dfc__lead-body', this.settings.leadBody));

    var form = document.createElement('form');
    form.className = 'gridos-dfc__form';
    form.noValidate = true;
    // IMPORTANT — do not reintroduce a native submit event on this form.
    //
    // HubSpot's "collected forms" scraper hooks the browser's `submit` event on
    // every <form> on the page. It fires before our validation runs, so a
    // visitor who typed a personal address and was rejected with "Please use
    // your work email address" was still harvested into HubSpot as a contact —
    // with data they never successfully submitted. That is junk in the CRM and
    // a consent problem, since they never completed the form.
    //
    // There is no per-form opt-out attribute, and the account-level toggle
    // cannot be used here because it also powers the footer newsletter capture.
    // So this form never fires a native submit event at all: the button is
    // type="button" and Enter is handled manually below. Both paths call
    // handleSubmit() directly.

    var summary = el('div', 'gridos-dfc__form-error');
    summary.setAttribute('role', 'alert');
    summary.setAttribute('tabindex', '-1');
    summary.hidden = true;
    form.appendChild(summary);

    var inputs = {};

    CFG.leadFields.forEach(function (def) {
      var fieldId = 'gridos-lead-' + def.id;
      var errId = fieldId + '-error';

      var field = el('div', 'gridos-dfc__field');

      var label = document.createElement('label');
      label.setAttribute('for', fieldId);
      label.className = 'gridos-dfc__label';
      label.appendChild(document.createTextNode(def.label));
      if (!def.required) {
        label.appendChild(el('span', 'gridos-dfc__optional', T('optionalSuffix')));
      }
      field.appendChild(label);

      var control;
      if (def.type === 'select') {
        control = document.createElement('select');
        def.options.forEach(function (opt) {
          var o = document.createElement('option');
          o.value = opt.value;
          o.textContent = opt.label;
          control.appendChild(o);
        });
      } else {
        control = document.createElement('input');
        control.type = def.type;
        if (def.autocomplete) control.autocomplete = def.autocomplete;
      }

      control.id = fieldId;
      control.name = def.id;
      control.className = 'gridos-dfc__input';
      if (def.required) control.required = true;
      control.setAttribute('data-hj-suppress', '');

      var err = el('p', 'gridos-dfc__field-error');
      err.id = errId;
      err.hidden = true;

      field.appendChild(control);
      field.appendChild(err);
      form.appendChild(field);

      var entry = { control: control, error: err, def: def };
      inputs[def.id] = entry;

      // Clear this field's error as soon as the visitor starts correcting it,
      // rather than making them press submit again to find out. We only clear
      // — we never re-validate mid-typing, which would flag a half-typed
      // email as invalid on every keystroke.
      var clearOnEdit = function () {
        if (entry.error.hidden) return;
        entry.error.hidden = true;
        entry.error.textContent = '';
        entry.control.removeAttribute('aria-invalid');
        entry.control.removeAttribute('aria-describedby');

        // Drop the summary once no field is still flagged.
        var stillBad = false;
        CFG.leadFields.forEach(function (other) {
          if (inputs[other.id] && !inputs[other.id].error.hidden) stillBad = true;
        });
        if (!stillBad) {
          summary.hidden = true;
          summary.textContent = '';
        }
      };

      control.addEventListener('input', clearOnEdit);
      control.addEventListener('change', clearOnEdit);
    });

    /* Consent ----------------------------------------------------------- */
    var consentWrap = el('div', 'gridos-dfc__consent');
    var consentInput = document.createElement('input');
    consentInput.type = 'checkbox';
    consentInput.id = 'gridos-lead-consent';
    consentInput.className = 'gridos-dfc__checkbox';
    consentInput.checked = false; // never pre-selected
    var consentLabel = document.createElement('label');
    consentLabel.setAttribute('for', 'gridos-lead-consent');
    consentLabel.className = 'gridos-dfc__consent-label';
    consentLabel.textContent = CFG.consent.marketing.label;
    consentWrap.appendChild(consentInput);
    consentWrap.appendChild(consentLabel);
    form.appendChild(consentWrap);

    /* Privacy ----------------------------------------------------------- */
    var privacy = el('p', 'gridos-dfc__privacy');
    privacy.appendChild(document.createTextNode(T('privacyBody')));
    if (this.settings.privacyUrl) {
      var privacyLink = document.createElement('a');
      privacyLink.href = this.settings.privacyUrl;
      privacyLink.textContent = T('privacyLink');
      privacyLink.className = 'gridos-dfc__privacy-link';
      privacy.appendChild(privacyLink);
    }
    form.appendChild(privacy);

    /* Submit ------------------------------------------------------------ */
    var submit = el('button', 'gridos-dfc__button gridos-dfc__button--primary gridos-dfc__button--wide', this.settings.leadSubmitLabel);
    // Deliberately NOT type="submit" — see the note at the top of this method.
    submit.type = 'button';
    form.appendChild(submit);

    function handleSubmit() {
      if (self.submitting || self.submitted) return;

      // Validate.
      var problems = [];
      var firstBad = null;

      CFG.leadFields.forEach(function (def) {
        var entry = inputs[def.id];
        var value = String(entry.control.value || '').trim();
        entry.error.hidden = true;
        entry.control.removeAttribute('aria-invalid');
        entry.control.removeAttribute('aria-describedby');

        function fail(message) {
          entry.error.textContent = message;
          entry.error.hidden = false;
          entry.control.setAttribute('aria-invalid', 'true');
          entry.control.setAttribute('aria-describedby', entry.error.id);
          problems.push(message);
          if (!firstBad) firstBad = entry.control;
        }

        if (def.required && !value) {
          fail(T('fieldRequired', { label: def.label }));
          return;
        }
        if (def.id === 'email' && value) {
          if (!isValidEmail(value)) {
            fail(T('emailInvalid'));
          } else if (isFreeMailDomain(value)) {
            fail(T('emailFreeMail'));
          }
        }
      });

      if (problems.length) {
        summary.hidden = false;
        summary.textContent = problems.length === 1
          ? T('formErrorOne', { problem: problems[0] })
          : T('formErrorMany', { count: problems.length });
        if (firstBad) firstBad.focus();
        return;
      }

      summary.hidden = true;

      var lead = {};
      CFG.leadFields.forEach(function (def) {
        lead[def.id] = String(inputs[def.id].control.value || '').trim();
      });

      self.submitLead(lead, consentInput.checked, submit, summary);
    }

    submit.addEventListener('click', handleSubmit);

    // Keep Enter-to-submit working without ever firing a native submit event.
    // preventDefault() also blocks the browser's implicit submission, which
    // would otherwise reach HubSpot's scraper.
    form.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter') return;
      var tag = event.target.tagName;
      if (tag !== 'INPUT' && tag !== 'SELECT') return;
      if (event.target.type === 'checkbox') return; // space/enter toggles it
      event.preventDefault();
      handleSubmit();
    });

    // Belt and braces: if anything ever dispatches submit programmatically,
    // stop it navigating. This does not cause the event, so the scraper is
    // still never triggered by us.
    form.addEventListener('submit', function (event) {
      event.preventDefault();
    });

    wrap.appendChild(form);
    return wrap;
  };

  /* ---------------------------------------------------------------------- */

  Assessment.prototype.submitLead = function (lead, consentGiven, submitButton, summary) {
    var self = this;
    var r = this.result;

    this.submitting = true;
    submitButton.disabled = true;
    var originalLabel = submitButton.textContent;
    submitButton.textContent = T('sending');
    this.announce(T('sendingAnnounce'));

    HubSpot.submit(this.settings, r, lead, this.attribution, consentGiven)
      .then(function (outcome) {
        if (!outcome.ok) throw new Error('HTTP ' + outcome.status);

        self.submitting = false;
        self.submitted = true;

        // The assessment is finished — remove the stored answers.
        Storage.clear();

        self.analytics.push(
          CFG.events.generateLead,
          Object.assign(
            { test_mode: outcome.testMode ? 'true' : 'false' },
            self.analytics.resultParams(r),
            // Campaign source for future GA4 use (non-personal, sanitised).
            self.analytics.utmParams(self.attribution)
          ),
          true
        );

        self.renderFullResult();
      })
      .catch(function (error) {
        self.submitting = false;
        submitButton.disabled = false;
        submitButton.textContent = originalLabel;

        // Non-personal diagnostics only.
        self.analytics.push(CFG.events.leadError, {
          error_type: 'hubspot_submission_failed',
          error_detail: String(error && error.message ? error.message : 'unknown').slice(0, 100)
        });

        summary.hidden = false;
        summary.textContent = T('submitFailed', { label: originalLabel });
        summary.focus && summary.focus();
        self.announce(T('submitFailedAnnounce'));

        if (self.settings.debug) {
          window.console.warn('[GRID-OS] HubSpot submission failed:', error && error.message);
        }
      });
  };

  /* ---------------------------------------------------------------------- */

  Assessment.prototype.renderFullResult = function () {
    var self = this;
    var r = this.result;

    clear(this.stageEl);
    this.stage = 'result';

    var panel = el('div', 'gridos-dfc__panel gridos-dfc__panel--full');

    var heading = el('h2', 'gridos-dfc__result-heading', T('scoreHeading'));
    heading.setAttribute('tabindex', '-1');
    panel.appendChild(heading);

    /* Score ------------------------------------------------------------- */
    var scoreWrap = el('div', 'gridos-dfc__score');
    var scoreValue = el('p', 'gridos-dfc__score-value');
    scoreValue.appendChild(el('span', 'gridos-dfc__score-number', String(r.totalScore)));
    scoreValue.appendChild(el('span', 'gridos-dfc__score-max', '/100'));
    scoreWrap.appendChild(scoreValue);
    scoreWrap.appendChild(el('p', 'gridos-dfc__score-profile', r.finalProfile.label));
    panel.appendChild(scoreWrap);

    if (r.criticalFriction) {
      panel.appendChild(el('p', 'gridos-dfc__flag', CFG.criticalOverride.flagLabel));
    }
    if (r.systemicFriction) {
      panel.appendChild(el('p', 'gridos-dfc__flag-note',
        'Three or more areas show critical friction, which suggests a systemic rather than local constraint.'));
    }

    panel.appendChild(el('p', 'gridos-dfc__result-headline', r.finalProfile.headline));
    panel.appendChild(el('p', 'gridos-dfc__result-body', r.finalProfile.body));

    var risk = el('p', 'gridos-dfc__result-risk');
    risk.appendChild(el('strong', null, T('riskLabel')));
    risk.appendChild(document.createTextNode(r.finalProfile.risk));
    panel.appendChild(risk);

    /* Five areas -------------------------------------------------------- */
    var areasSection = el('section', 'gridos-dfc__areas');
    areasSection.appendChild(el('h3', 'gridos-dfc__subheading', T('areasHeading')));

    var table = el('ul', 'gridos-dfc__area-list');

    r.areas.forEach(function (area) {
      var item = el('li', 'gridos-dfc__area gridos-dfc__area--' + area.bandId);

      var head = el('div', 'gridos-dfc__area-head');
      head.appendChild(el('span', 'gridos-dfc__area-name', area.number + ' — ' + area.name));
      head.appendChild(el('span', 'gridos-dfc__area-score', area.score + '/20'));
      item.appendChild(head);

      // The bar is decorative; the score and band label carry the meaning, so
      // nothing here depends on colour alone.
      var track = el('div', 'gridos-dfc__bar-track');
      track.setAttribute('aria-hidden', 'true');
      var fill = el('div', 'gridos-dfc__bar-fill');
      fill.style.width = area.percent + '%';
      track.appendChild(fill);
      item.appendChild(track);

      var badge = el('p', 'gridos-dfc__area-band');
      badge.appendChild(el('span', 'gridos-dfc__band-label', area.bandLabel));
      item.appendChild(badge);

      item.appendChild(el('p', 'gridos-dfc__area-title', area.result.title));
      item.appendChild(el('p', 'gridos-dfc__area-body', area.result.body));

      table.appendChild(item);
    });

    areasSection.appendChild(table);
    panel.appendChild(areasSection);

    /* Highlights -------------------------------------------------------- */
    var highlights = el('section', 'gridos-dfc__highlights');

    function highlight(label, area, tied) {
      var box = el('div', 'gridos-dfc__highlight');
      box.appendChild(el('p', 'gridos-dfc__highlight-label', label));
      box.appendChild(el('p', 'gridos-dfc__highlight-area', area.name));
      box.appendChild(el('p', 'gridos-dfc__highlight-score', area.score + '/20 · ' + area.bandLabel));
      if (tied) {
        box.appendChild(el('p', 'gridos-dfc__highlight-tie', T('tiedNote')));
      }
      return box;
    }

    if (r.allAreasEqual) {
      var even = el('p', 'gridos-dfc__even-note', T('evenNote'));
      highlights.appendChild(even);
    } else {
      highlights.appendChild(highlight(T('strongestLabel'), r.strongest, r.strongestTied));
      highlights.appendChild(highlight(T('primaryFrictionLabel'), r.primaryFriction, r.primaryTied));
      highlights.appendChild(highlight(T('secondaryFrictionLabel'), r.secondaryFriction, r.secondaryTied));
    }

    panel.appendChild(highlights);

    /* Priorities -------------------------------------------------------- */
    var prio = el('section', 'gridos-dfc__priorities');
    prio.appendChild(el('h3', 'gridos-dfc__subheading', T('prioritiesHeading')));
    var ol = el('ol', 'gridos-dfc__priority-list');
    r.recommendations.forEach(function (rec) {
      ol.appendChild(el('li', 'gridos-dfc__priority', rec));
    });
    prio.appendChild(ol);

    var nextAction = el('p', 'gridos-dfc__next-action');
    nextAction.appendChild(el('strong', null, T('startHereLabel')));
    nextAction.appendChild(document.createTextNode(r.nextAction));
    prio.appendChild(nextAction);

    panel.appendChild(prio);

    /* CTAs -------------------------------------------------------------- */
    var ctas = el('div', 'gridos-dfc__ctas');

    if (this.settings.ctaUrl) {
      var cta = document.createElement('a');
      cta.className = 'gridos-dfc__button gridos-dfc__button--primary';
      cta.href = this.settings.ctaUrl;
      cta.textContent = this.settings.ctaLabel;
      cta.addEventListener('click', function () {
        self.analytics.push(CFG.events.scoreReview, Object.assign(
          { cta_placement: 'result_primary' },
          self.analytics.resultParams(r)
        ));
        self.analytics.push(CFG.events.schedulerView, self.analytics.resultParams(r), true);
      });
      ctas.appendChild(cta);
    }

    if (this.settings.secondaryCtaUrl && this.settings.secondaryCtaLabel) {
      var cta2 = document.createElement('a');
      cta2.className = 'gridos-dfc__button gridos-dfc__button--ghost';
      cta2.href = this.settings.secondaryCtaUrl;
      cta2.textContent = this.settings.secondaryCtaLabel;
      cta2.addEventListener('click', function () {
        self.analytics.push(CFG.events.ctaClick, Object.assign(
          { cta_placement: 'result_secondary' },
          self.analytics.resultParams(r)
        ));
      });
      ctas.appendChild(cta2);
    }

    panel.appendChild(ctas);

    /* Restart ----------------------------------------------------------- */
    var restart = el('button', 'gridos-dfc__restart', T('restart'));
    restart.type = 'button';
    restart.addEventListener('click', function () {
      self.analytics.push(CFG.events.restart, self.analytics.resultParams(r));
      self.answers = {};
      self.result = null;
      self.submitted = false;
      Storage.clear();
      clear(self.stageEl);
      if (self.introEl) self.introEl.hidden = false;
      self.stage = 'intro';
      var firstStart = self.startButtons[0];
      if (firstStart) firstStart.focus();
      self.announce(T('restartAnnounce'));
    });
    panel.appendChild(restart);

    var disclaimer = el('p', 'gridos-dfc__disclaimer', T('disclaimer'));
    panel.appendChild(disclaimer);

    this.stageEl.appendChild(panel);

    this.analytics.push(CFG.events.fullResultView, this.analytics.resultParams(r), true);

    heading.focus();
    this.announce(T('fullResultAnnounce', {
      score: r.totalScore,
      profile: r.finalProfile.label,
      area: r.primaryFriction.name
    }));
  };

  /* =========================================================================
   * 8. Custom element registration.
   * ====================================================================== */

  function defineElement() {
    if (window.customElements.get('grid-os-assessment')) return;

    window.customElements.define('grid-os-assessment', class extends HTMLElement {
      connectedCallback() {
        // Guard against double initialisation if the element is moved in the
        // DOM or the theme editor re-renders the section.
        if (this.__gridosReady) return;
        this.__gridosReady = true;

        try {
          this.__gridos = new Assessment(this);
          this.classList.add('gridos-dfc--ready');
        } catch (error) {
          this.classList.add('gridos-dfc--failed');
          var fallback = this.querySelector('[data-gridos-fallback]');
          if (fallback) fallback.hidden = false;
        }
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', defineElement);
  } else {
    defineElement();
  }

  // Exported for tests and for the QA harness.
  return {
    Scoring: Scoring,
    Attribution: Attribution,
    HubSpot: HubSpot,
    Summary: Summary,
    Storage: Storage,
    config: CFG
  };
})();